// @charset "utf-8"
import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface CountUpProps {
  target: number;
  suffix?: string;
  duration?: number;
  inView: boolean;
}

const STATS = [
  { value: 25, suffix: "+", label: "sources aggregated", accent: "#4B8EF0", countUp: true },
  { value: "Top 100", label: "ranked candidates per role", accent: "#34D17A", countUp: false },
  { value: "Seconds", label: "to an AI-ranked shortlist", accent: "#F5C842", countUp: false },
  { value: "Zero", label: "tab-switching required", accent: "#4B8EF0", countUp: false },
] as const;

function CountUp({ target, suffix = "", duration = 1500, inView }: CountUpProps) {
  // Initial state = the real value: crawlers (and no-scroll renders) must never
  // index "0+ sources / 0% lower cost". The count-up is a scroll-triggered
  // enhancement only.
  const [display, setDisplay] = useState(target);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
      else setDisplay(target);
    };
    requestAnimationFrame(tick);
  }, [inView, target, duration]);

  return <>{display}{suffix}</>;
}

export default function Stats() {
  const ref = useRef<HTMLElement | null>(null);
  const isMobile = useIsMobile();
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} style={{ background: "rgba(13,15,30,0.6)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, 1fr)" }}>
        {STATS.map((stat, index) => (
          <div key={index} style={{ display: "flex" }}>
            <motion.div initial={{ y: 16 }} animate={inView ? { y: 0 } : {}} transition={{ duration: 0.55, delay: index * 0.1, ease: [0.25, 0.1, 0.25, 1] }} style={{ flex: 1, padding: isMobile ? "1.75rem 1rem" : "2.5rem 2rem", textAlign: "center" }}>
              <div style={{ width: 28, height: 2, borderRadius: 2, background: stat.accent, margin: "0 auto 1rem" }} />
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "3rem", color: "#F4F5FA", lineHeight: 1 }}>
                {stat.countUp ? <CountUp target={stat.value as number} suffix={stat.suffix} inView={inView} /> : stat.value}
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.82rem", color: "#8891AA", marginTop: "0.4rem" }}>{stat.label}</div>
            </motion.div>
            {index < STATS.length - 1 && !isMobile && <div style={{ width: 1, background: "rgba(255,255,255,0.06)", height: 60, alignSelf: "center" }} />}
          </div>
        ))}
      </div>
    </section>
  );
}
